import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";

const CustomerTable = ({
  customers,
  onEdit,
  onDelete,
  onView,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSort,
}) => {
  const { t } = useTranslation();
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);

  /* =======================
   * Helpers
   ======================= */
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getStatusBadge = (status) => {
    const map = {
      active: "bg-success text-success-foreground",
      inactive: "bg-secondary text-secondary-foreground",
      pending: "bg-warning text-warning-foreground",
      blocked: "bg-error text-error-foreground",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          map[status] || map.active
        }`}
      >
        {t(status)}
      </span>
    );
  };

  /* =======================
   * Fields Definition (🔥 الأساس)
   ======================= */
  const fields = [
    {
      key: "name",
      label: t("customerName"),
      render: (c) => `${c.first_name} ${c.last_name}`,
      mobilePrimary: true,
    },
    {
      key: "email",
      label: t("email"),
      icon: "Mail",
      render: (c) => c.email,
    },
    {
      key: "phone",
      label: t("phone"),
      icon: "Phone",
      render: (c) => c.phone,
    },
    {
      key: "province",
      label: t("province"),
      render: (c) => c.province,
    },
    {
      key: "role",
      label: t("الصلاحية"),
      render: (c) => t(c.role.name),
    },
    {
      key: "status",
      label: t("status"),
      render: (c) => getStatusBadge(c.role.name),
      mobileHeader: true,
    },
    {
      key: "lastContact",
      label: t("lastContact"),
      icon: "Calendar",
      render: (c) => formatDate(c.lastContact || new Date()),
    },
  ];

  /* =======================
   * Data
   ======================= */
  const filteredCustomers = useMemo(() => {
    return customers.filter(
      (c) =>
        `${c.first_name} ${c.last_name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm),
    );
  }, [customers, searchTerm]);

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  /* =======================
   * Render
   ======================= */
  return (
    <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">سجلات المشرفين</h3>
        <Input
          type="search"
          placeholder={t("searchCustomers")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64"
        />
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              {fields.map((f) => (
                <th
                  key={f.key}
                  onClick={() => onSort(f.key)}
                  className="px-4 py-3 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-1">
                    {f.label}
                    {sortConfig.key === f.key && (
                      <Icon
                        name={
                          sortConfig.direction === "asc"
                            ? "ChevronUp"
                            : "ChevronDown"
                        }
                        size={14}
                      />
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-center">{t("actions")}</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {paginatedCustomers.map((c) => (
              <tr
                key={c.id}
                onClick={() => onView(c)}
                className="hover:bg-muted cursor-pointer"
              >
                {fields.map((f) => (
                  <td key={f.key} className="px-4 py-3">
                    {f.render(c)}
                  </td>
                ))}
                <td
                  className="px-4 py-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-center gap-2">
                    <Icon name="Eye" size={16} onClick={() => onView(c)} />
                    <Icon name="Edit" size={16} onClick={() => onEdit(c)} />
                    <Icon
                      name="Trash2"
                      size={16}
                      className="text-error"
                      onClick={() => onDelete(c)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y">
        {paginatedCustomers.map((c) => (
          <div key={c.id} className="p-4 space-y-3">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div className="font-medium">
                {fields.find((f) => f.mobilePrimary)?.render(c)}
              </div>
              {fields.find((f) => f.mobileHeader)?.render(c)}
            </div>

            {/* Fields */}
            {fields
              .filter((f) => !f.mobilePrimary && !f.mobileHeader)
              .map((f) => (
                <div key={f.key} className="flex items-center gap-2 text-sm">
                  {f.icon && (
                    <Icon
                      name={f.icon}
                      size={14}
                      className="text-muted-foreground"
                    />
                  )}
                  <span>{f.render(c)}</span>
                </div>
              ))}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button onClick={() => onView(c)}>
                <Icon name="Eye" size={16} />
              </button>
              <button onClick={() => onEdit(c)}>
                <Icon name="Edit" size={16} />
              </button>
              <button onClick={() => onDelete(c)}>
                <Icon name="Trash2" size={16} className="text-error" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerTable;
